import { tempoApi } from '@/lib/api/tempoApi';
import type {
  BacklogItem,
  BacklogItemBulkEditCommitRequest,
  BacklogItemBulkEditStageRequest,
  BacklogItemDataSet,
  BacklogItemPatchRequest,
  BacklogItemSelectionCommitRequest,
  BacklogItemSelectionResponse,
  BacklogItemSelectionStageRequest,
} from '@/lib/api/types/adaptive-planner/backlog-item';

type BacklogItemParams = { planId: number };

export type BacklogItemFilters = {
  teamIds?: number[];
  noTeamSet?: boolean;
  estimationIds?: number[];
  itemTypeIds?: number[];
  itemStatusIds?: number[];
  needsTeamOrEstimate?: boolean;
  isMLImputed?: boolean;
};

const buildBacklogItemQueryParams = (filters?: BacklogItemFilters): string => {
  if (!filters) return '';

  const params = new URLSearchParams();

  if (filters.teamIds && filters.teamIds.length > 0) {
    params.set('teamIds', filters.teamIds.join(','));
  }
  if (filters.noTeamSet) {
    params.set('noTeamSet', 'true');
  }
  if (filters.estimationIds && filters.estimationIds.length > 0) {
    params.set('estimationIds', filters.estimationIds.join(','));
  }
  if (filters.itemTypeIds && filters.itemTypeIds.length > 0) {
    params.set('itemTypeIds', filters.itemTypeIds.join(','));
  }
  if (filters.itemStatusIds && filters.itemStatusIds.length > 0) {
    params.set('itemStatusIds', filters.itemStatusIds.join(','));
  }
  if (filters.needsTeamOrEstimate) {
    params.set('needsTeamOrEstimate', 'true');
  }
  if (filters.isMLImputed) {
    params.set('isMLImputed', 'true');
  }

  return params.toString() ? `?${params.toString()}` : '';
};

export const bulkFetchBacklogItems = async ({
  planId,
  itemIds,
}: BacklogItemParams & { itemIds: number[] }) => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/backlog-items/bulk-fetch`, {
      json: itemIds,
    })
    .json<BacklogItem[]>();
  return response;
};

const fetchBacklogItemsDataSet = async ({
  planId,
  filters,
}: BacklogItemParams & { filters?: BacklogItemFilters }) => {
  const queryParams = buildBacklogItemQueryParams(filters);
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/backlog-items/data-set${queryParams}`)
    .json<BacklogItemDataSet[]>();
  return response;
};

const updateBacklogItem = async ({
  itemStoreId,
  planId,
  data,
}: BacklogItemParams & {
  itemStoreId: number;
  data: BacklogItemPatchRequest;
}) => {
  const response = await tempoApi()
    .patch(`api/v1/adaptive-planner/plans/${planId}/backlog-items/${itemStoreId}`, {
      json: data,
    })
    .json<BacklogItem>();
  return response;
};

const fetchBacklogItemsAssignedTeamIds = async ({ planId }: BacklogItemParams) => {
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/backlog-items/assigned-team-ids`)
    .json<number[]>();
  return response;
};

const stageSelection = async ({
  planId,
  sessionId,
  itemIds,
}: BacklogItemParams & BacklogItemSelectionStageRequest): Promise<void> => {
  await tempoApi().post(`api/v1/adaptive-planner/plans/${planId}/backlog-items/selection/stage`, {
    json: { sessionId, itemIds },
  });
};

const commitSelection = async ({
  planId,
  sessionId,
}: BacklogItemParams &
  BacklogItemSelectionCommitRequest): Promise<BacklogItemSelectionResponse> => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/backlog-items/selection/commit`, {
      json: { sessionId },
    })
    .json<BacklogItemSelectionResponse>();
  return response;
};

const fetchBacklogItemsCount = async ({
  planId,
  filters,
}: BacklogItemParams & { filters?: BacklogItemFilters }): Promise<number> => {
  const queryParams = buildBacklogItemQueryParams(filters);
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/backlog-items/count${queryParams}`)
    .json<number>();
  return response;
};

const stageBulkEdit = async ({
  planId,
  sessionId,
  request,
}: BacklogItemParams & BacklogItemBulkEditStageRequest): Promise<void> => {
  await tempoApi().post(`api/v1/adaptive-planner/plans/${planId}/backlog-items/bulk-edit/stage`, {
    json: { sessionId, request },
  });
};

const commitBulkEdit = async ({
  planId,
  sessionId,
}: BacklogItemParams & BacklogItemBulkEditCommitRequest): Promise<BacklogItem[]> => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/backlog-items/bulk-edit/commit`, {
      json: { sessionId },
    })
    .json<BacklogItem[]>();
  return response;
};

export const AdaptivePlannerBacklogItemApi = {
  bulkFetchBacklogItems,
  fetchBacklogItemsDataSet,
  fetchBacklogItemsAssignedTeamIds,
  updateBacklogItem,
  stageSelection,
  commitSelection,
  stageBulkEdit,
  commitBulkEdit,
  fetchBacklogItemsCount,
} as const;
